import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { VASLinearPlot } from '../visualizations';

const GRAPH_HEIGHT = 70;
const MARKER_Y_OFFSET = GRAPH_HEIGHT - 10;
const MARKER_JITTER = 10;

const USER_MARKER = Object.freeze({
    fill: 'red',
    stroke: '#fff',
    strokeWidth: 1.5,
    radius: 5,
});

const SESSION_MARKER = Object.freeze({
    fill: 'none',
    stroke: 'red',
    strokeWidth: 2,
    radius: 4,
});

const SHARED_MARKER = Object.freeze({
    fill: '#209385',
    stroke: '#fff',
    strokeWidth: 1,
    radius: 5,
});

const PVQD_MARKER = Object.freeze({
    fill: '#f39c12',
    stroke: '#fff',
    strokeWidth: 1,
    radius: 5,
});

const SOURCE_MARKER = PVQD_MARKER;

const buildMarker = (id, value, style) => ({
    id,
    value,
    yOffset: MARKER_Y_OFFSET,
    jitter: MARKER_JITTER,
    ...style,
});

const VASGraph = ({
    attributeName,
    ariaLabel = null,
    userValues = [],
    sharedValues = [],
    sessionValues = [],
    consensusValues = [],
    labelT = null,
    allVoicedLegendLabel = null,
    sessionLegendLabel = null,
    consensusLegendLabel = null,
    sessionLegendHelp = null,
    consensusLegendHelp = null
}) => {
    const { t } = useTranslation();
    const feedbackT = labelT || t;

    const markers = useMemo(() => [
        ...userValues.map(({ value }, index) =>
            buildMarker(`${attributeName}-user-${index}`, value, USER_MARKER)
        ),
        ...sharedValues.map(({ value, raterSource, feedbackPresentation }, index) =>
            buildMarker(
                `${attributeName}-shared-${index}`,
                value,
                feedbackPresentation?.role === 'source' || raterSource === 'pvqdRater'
                    ? SOURCE_MARKER
                    : SHARED_MARKER
            )
        ),
        ...sessionValues.map(({ value }, index) =>
            buildMarker(`${attributeName}-session-${index}`, value, SESSION_MARKER)
        ),
        ...consensusValues.map(({ value }, index) =>
            buildMarker(`${attributeName}-consensus-${index}`, value, USER_MARKER)
        ),
    ], [attributeName, consensusValues, sessionValues, sharedValues, userValues]);

    const legendItems = useMemo(() => {
        const hasUserEvaluations = userValues.length > 0;
        const hasSessionEvaluations = sessionValues.length > 0;
        const hasConsensusEvaluation = consensusValues.length > 0;
        const sharedPresentationItems = Array.from(
            new Map(
                sharedValues.map(({ raterSource, feedbackPresentation }) => {
                    const isSource =
                        feedbackPresentation?.role === 'source' ||
                        raterSource === 'pvqdRater';
                    const key = feedbackPresentation?.key ||
                        (isSource ? 'pvqd' : 'allvoiced-peer');
                    const label = feedbackPresentation?.label ||
                        (isSource
                            ? feedbackT('pvqd_evaluations')
                            : allVoicedLegendLabel ||
                              feedbackT('other_allvoiced_users_evaluations'));

                    return [key, {
                        id: `shared-${key}`,
                        label,
                        help: feedbackPresentation?.help || null,
                        marker: isSource
                            ? { fill: '#f39c12', stroke: '#f39c12', strokeWidth: 2, radius: 5 }
                            : { fill: '#209385', stroke: '#209385', strokeWidth: 2, radius: 5 },
                    }];
                })
            ).values()
        );

        return [
            ...(hasUserEvaluations ? [{
                id: 'user-evaluation',
                label: feedbackT('feedback_your_rating', { defaultValue: 'Your rating' }),
                marker: { fill: 'red', stroke: 'red', strokeWidth: 2, radius: 5 },
            }] : []),
            ...(hasSessionEvaluations ? [{
                id: 'session-evaluations',
                label: sessionLegendLabel || feedbackT('session_evaluations'),
                help: sessionLegendHelp,
                marker: { fill: 'none', stroke: 'red', strokeWidth: 2, radius: 5 },
            }] : []),
            ...(hasConsensusEvaluation ? [{
                id: 'consensus-evaluation',
                label: consensusLegendLabel || feedbackT('consensus_evaluation'),
                help: consensusLegendHelp,
                marker: { fill: 'red', stroke: 'red', strokeWidth: 2, radius: 5 },
            }] : []),
            ...sharedPresentationItems,
        ];
    }, [
      allVoicedLegendLabel,
      consensusValues,
      feedbackT,
      consensusLegendLabel,
      consensusLegendHelp,
      sessionValues,
      sessionLegendLabel,
      sessionLegendHelp,
      sharedValues,
      userValues
    ]);

    return (
        <div className="vas-graph__composition" data-testid="vas-graph-composition">
            <div className="vas-graph__plot" data-testid="vas-graph-plot">
                <VASLinearPlot
                    ariaLabel={ariaLabel || `${attributeName || 'VAS'} graph`}
                    markers={markers}
                    legendItems={[]}
                />
            </div>
            <div
                className="vas-graph__legend"
                role="group"
                aria-label={feedbackT('feedback_evidence_groups', {
                    defaultValue: 'Evidence groups',
                })}
            >
                {legendItems.map((item) => (
                    <div
                        key={item.id}
                        className="vas-graph__legend-item"
                    >
                        <span
                            aria-hidden="true"
                            className="vas-graph__legend-marker"
                            style={{
                                background: item.marker?.fill === 'none' ? 'transparent' : item.marker?.fill,
                                border: `${item.marker?.strokeWidth || 2}px solid ${item.marker?.stroke || item.marker?.fill}`,
                            }}
                        />
                        <span>{item.label}</span>
                        {item.help ? <span> {item.help}</span> : null}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default VASGraph;
